import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { B } from '../../constants/brand';
import { Btn } from '../ui';
import { naicsLabel } from '@harvus/shared';

// The deal header's NAICS line: what industry this deal is in, where that came
// from, and a way to ask for it now.
//
// It says WHICH of the two sources set the code, because they mean different
// things: a code someone typed is a decision, and one derived from the
// documents is a reading that the sweep will revisit when the documents change.
// A code shown without that distinction invites both from being trusted equally.
//
// Degrades to rendering NOTHING at all rather than an error: on a pre-169 schema
// the endpoint answers 503 with a reason, and a deal header is the wrong place
// to report a missing migration.
export const DealNaicsChip = ({ dealId, canEdit = false, onChanged }) => {
  const [naics, setNaics]   = useState(null);
  const [busy, setBusy]     = useState(false);
  const [note, setNote]     = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get(`deals/${dealId}/naics`);
      setNaics(d?.available ? d : null);
    } catch { setNaics(null); }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const derive = async (force = false) => {
    setBusy(true); setNote(null);
    try {
      const r = await api.post(`deals/${dealId}/naics/classify`, { force });
      // `ok: false` here is an answer ABOUT THE DEAL — "its documents have not
      // been read yet" — not a server fault, so it is shown as a note rather
      // than swallowed or thrown.
      if (!r?.ok) setNote(r?.reason || 'Could not classify this deal.');
      else if (r.status === 'unknown') setNote('The documents on this deal do not say what industry it is in.');
      await load();
      onChanged?.();
    } catch (e) {
      setNote(e.message || 'Classification failed.');
    } finally { setBusy(false); }
  };

  if (!naics) return null;

  const label = naicsLabel(naics.code, naics.description);
  const fromAi = naics.source === 'ai';

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 600 }}>NAICS:</span>
      {label ? (
        <>
          <span title={fromAi ? 'Derived from this deal’s documents' : 'Set by hand'}>{label}</span>
          <span style={{
            fontSize: 11, padding: '1px 6px', borderRadius: 10,
            border: `1px solid ${B.border}`, color: B.textLt,
          }}>
            {fromAi
              ? `AI${naics.confidence != null ? ` · ${Math.round(naics.confidence * 100)}%` : ''}`
              : 'Entered'}
          </span>
          {naics.stale && fromAi && (
            <span style={{ fontSize: 11, color: B.textLt }}>
              (documents have changed since)
            </span>
          )}
        </>
      ) : (
        <span style={{ color: B.textLt }}>
          {naics.status === 'unknown' ? 'not determinable from the documents' : 'not set'}
        </span>
      )}

      {canEdit && (naics.source !== 'user') && (
        <Btn variant="ghost" size="sm" disabled={busy} onClick={() => derive(!!naics.code)}>
          {busy ? 'Reading…' : (naics.code ? 'Re-derive' : 'Derive from documents')}
        </Btn>
      )}
      {note && <span style={{ fontSize: 12, color: B.textLt }}>{note}</span>}
    </span>
  );
};
